/* Dual cart system:
 * - Two carts in localStorage: cart_us, cart_cn
 * - Intercept product-form submits and route to the appropriate cart
 * - Only sync to Shopify's real cart on checkout
 * - Detects warehouse from product tags (warehouse-us, warehouse-cn)
 */

(() => {
  const STORAGE_KEYS = { us: 'cart_us', cn: 'cart_cn' };
  const ACTIVE_KEY = 'activeWarehouse';

  const normalizeWarehouse = (raw) => {
    const s = String(raw || '').toLowerCase();
    if (s.includes('us')) return 'us';
    if (s.includes('cn') || s.includes('china')) return 'cn';
    return localStorage.getItem(ACTIVE_KEY) || 'us';
  };

  // Extract product data from the current page
  const extractProductDataFromPage = (form) => {
    try {
      const productSection = form.closest('[data-section-type="product"]') || 
                             form.closest('.product') ||
                             form.closest('product-info');
      
      if (productSection) {
        // Try to get product title
        const titleEl = productSection.querySelector('.product__title, .product-title, h1');
        const product_title = titleEl?.textContent?.trim() || 'Product';
        
        // Try to get price (prefer sale price if available)
        const priceEl = productSection.querySelector('.price-item--sale, .price-item--regular, .price__regular, [data-product-price], [data-product-price-value]');
        const rawText = priceEl?.getAttribute('data-product-price-value') || priceEl?.textContent || '';
        const match = String(rawText).replace(/\s+/g, ' ').match(/[0-9]+(?:,[0-9]{3})*(?:\.[0-9]{2})?/);
        const price = match ? Math.round(parseFloat(match[0].replace(/,/g, '')) * 100) : 0;
        
        // Try to get variant title - exclude "sold out or unavailable" text
        let variant_title = '';
        const checkedInput = productSection.querySelector('.product-form__input input:checked');
        if (checkedInput) {
          const label = productSection.querySelector(`label[for="${checkedInput.id}"]`);
          if (label) {
            // Get text but exclude visually-hidden or label-unavailable spans
            const labelClone = label.cloneNode(true);
            labelClone.querySelectorAll('.visually-hidden, .label-unavailable').forEach(el => el.remove());
            variant_title = labelClone.textContent?.trim() || '';
          }
        } else {
          // Try dropdown/select option
          const selectOption = productSection.querySelector('.product-variant-select option:checked, select[name="id"] option:checked');
          if (selectOption) {
            // For dropdowns, use the value attribute or clean text content
            variant_title = selectOption.value || selectOption.textContent?.trim() || '';
            // Clean up "unavailable" text patterns
            variant_title = variant_title.replace(/\s*-\s*(sold out|unavailable|out of stock).*/i, '').trim();
          }
        }
        
        // Try to get image
        const imageEl = productSection.querySelector('.product__media img, .product-image img, .featured-image img');
        const image = imageEl?.src || imageEl?.dataset?.src || null;
        
        // Get URL
        const url = window.location.pathname;
        
        return {
          product_title,
          variant_title,
          price,
          image,
          url
        };
      }
    } catch (e) {
      console.error('[Dual Cart] Error extracting product data:', e);
    }
    
    return {
      product_title: 'Product',
      price: 0,
      variant_title: '',
      image: null,
      url: window.location.pathname
    };
  };

  // Detect warehouse from product tags on the page
  const detectWarehouseFromTags = (form) => {
    try {
      // Look for product tags in the DOM
      const productSection = form.closest('[data-section-type="product"]') || 
                           form.closest('.product') ||
                           form.closest('product-info');
      
      if (productSection) {
        // Check for tag elements (common in product templates)
        const tagElements = productSection.querySelectorAll('.product__tag, .product-tag, [data-product-tags]');
        for (const tagEl of tagElements) {
          const tagText = (tagEl.textContent || tagEl.dataset.productTags || '').toLowerCase();
          if (tagText.includes('warehouse-us') || tagText.includes('warehouse_us')) return 'us';
          if (tagText.includes('warehouse-cn') || tagText.includes('warehouse_cn')) return 'cn';
        }

        // Check data attributes
        const tagsAttr = productSection.dataset.tags || productSection.dataset.productTags || '';
        const tagsLower = tagsAttr.toLowerCase();
        if (tagsLower.includes('warehouse-us') || tagsLower.includes('warehouse_us')) return 'us';
        if (tagsLower.includes('warehouse-cn') || tagsLower.includes('warehouse_cn')) return 'cn';
      }

      // Check form's product-form parent for data-warehouse attribute
      const productForm = form.closest('product-form');
      if (productForm?.dataset.warehouse) {
        return normalizeWarehouse(productForm.dataset.warehouse);
      }

      return null;
    } catch (err) {
      console.error('Error detecting warehouse from tags:', err);
      return null;
    }
  };

  const read = (w) => {
    const key = STORAGE_KEYS[w] || STORAGE_KEYS.us;
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  };
  const write = (w, data) => localStorage.setItem(STORAGE_KEYS[w], JSON.stringify(data));

  const api = (window.WarehouseCart = window.WarehouseCart || {});
  api.getActiveWarehouse = () => localStorage.getItem(ACTIVE_KEY) || 'us';
  api.setActiveWarehouse = (w) => {
    const wh = normalizeWarehouse(w);
    localStorage.setItem(ACTIVE_KEY, wh);
    api.renderCounts();
  };
  api.getCart = (w) => read(normalizeWarehouse(w));
  api.count = (w) => api.getCart(w).reduce((s, i) => s + Number(i.quantity || 1), 0);

  // Update: ensure we fill missing item data using the variant JSON endpoint
  api.addItem = async (w, { id, quantity = 1, properties = {}, product_title, variant_title, price, image, url }) => {
    const wh = normalizeWarehouse(w);
    const cart = read(wh);
    const existing = cart.find((i) => String(i.id) === String(id));
  
    // Add warehouse to properties for Shopify line item tracking
    const warehouseLabel = wh === 'us' ? 'warehouse-us' : 'warehouse-cn';
    const itemProperties = {
      ...properties,
      'Warehouse': warehouseLabel
    };
  
    if (existing) {
      existing.quantity = Number(existing.quantity || 0) + Number(quantity || 1);
      existing.properties = itemProperties;
      if (!existing.price || existing.price <= 0 || !existing.image || !existing.variant_title) {
        const variantInfo = await fetchVariantInfo(id);
        Object.assign(existing, variantInfo);
        if (!existing.price || existing.price <= 0 || !existing.image) {
          const cartInfo = await fetchProductInfo(id);
          Object.assign(existing, cartInfo);
        }
      }
    } else {
      // Prefer DOM data, but enrich with variant JSON if important fields are missing
      let productInfo = { product_title, variant_title, price, image, url };
      const needsVariantFetch =
        !productInfo ||
        !productInfo.price ||
        productInfo.price <= 0 ||
        !productInfo.image ||
        !productInfo.variant_title;
  
      if (needsVariantFetch) {
        const variantInfo = await fetchVariantInfo(id);
        productInfo = { ...productInfo, ...variantInfo };
        if (!productInfo.price || productInfo.price <= 0 || !productInfo.image) {
          const cartInfo = await fetchProductInfo(id);
          productInfo = { ...productInfo, ...cartInfo };
        }
      }
  
      cart.push({
        id,
        quantity: Number(quantity || 1),
        properties: itemProperties,
        ...productInfo
      });
    }
  
    write(wh, cart);
    api.renderCounts();
  };

  // Fetch product information using Shopify's cart API
  const fetchVariantInfo = async (variantId) => {
    try {
      const res = await fetch(`/variants/${variantId}.json`, {
        headers: { Accept: 'application/json' }
      });

      if (res.ok) {
        const data = await res.json();
        // Some shops return { variant: { ... } }, others return the object directly
        const v = data.variant || data;
        // Price handling: prefer cents if present, otherwise parse
        let priceCents = 0;
        const raw = v.price ?? v.presentment_prices?.[0]?.price?.amount ?? v.compare_at_price;
        if (typeof raw === 'number') {
          priceCents = raw % 1 !== 0 ? Math.round(raw * 100) : Math.round(raw);
        } else {
          const s = String(raw || '').trim();
          if (/^\d+$/.test(s)) {
            priceCents = parseInt(s, 10);
          } else {
            priceCents = Math.round(parseFloat(s.replace(/[^0-9.]/g, '')) * 100) || 0;
          }
        }

        // Image handling: featured_image can be a string or an object
        let imageUrl = null;
        if (v.featured_image) {
          imageUrl =
            typeof v.featured_image === 'string'
              ? v.featured_image
              : v.featured_image.src || null;
        }
        if (!imageUrl && v.image) {
          imageUrl =
            typeof v.image === 'string' ? v.image : v.image.src || null;
        }

        return {
          // Keep product_title from DOM when available
          variant_title: v.title || '',
          price: priceCents || 0,
          image: imageUrl || null
        };
      } else if (res.status === 404) {
        return null;
      } else {
        console.error('[Dual Cart] Variant JSON fetch failed:', res.status);
      }
    } catch (e) {
      console.error('[Dual Cart] Variant JSON error:', e);
    }

    // Fallback, avoids breaking the UI
    return { variant_title: '', price: 0, image: null };
  };

  const fetchProductInfo = async (variantId) => {
    try {
      console.log('[Dual Cart] Fetching product info for variant:', variantId);
      
      // Add to actual cart to get data
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          items: [{ id: variantId, quantity: 1 }]
        })
      });
      
      if (addResponse.ok) {
        const addData = await addResponse.json();
        console.log('[Dual Cart] Add response:', addData);
        
        // Extract item data - could be single item or array
        const itemData = Array.isArray(addData.items) ? addData.items[0] : addData;
        
        // Immediately remove from actual cart
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: variantId, quantity: 0 })
        });
        
        const productInfo = {
          product_id: itemData.product_id,
          product_title: itemData.product_title || itemData.title,
          variant_title: itemData.variant_title || itemData.variant_title || '',
          price: itemData.price || itemData.final_price || 0,
          image: itemData.image || itemData.featured_image?.url || null,
          url: itemData.url || `/products/${itemData.handle || ''}`,
          handle: itemData.handle
        };
        
        console.log('[Dual Cart] Extracted product info:', productInfo);
        return productInfo;
      } else {
        const errorText = await addResponse.text();
        console.error('[Dual Cart] Add to cart failed:', addResponse.status, errorText);
      }
    } catch (e) {
      console.error('[Dual Cart] Error fetching product info:', e);
    }
    
    // Fallback: return minimal data
    console.warn('[Dual Cart] Using fallback data for variant:', variantId);
    return {
      product_title: `Product (ID: ${variantId})`,
      price: 0,
      variant_title: '',
      image: null,
      url: '#'
    };
  };

  api.removeItem = (w, id) => {
    const wh = normalizeWarehouse(w);
    const cart = read(wh).filter((i) => String(i.id) !== String(id));
    write(wh, cart);
    api.renderCounts();
    // If on cart page, trigger re-render
    if (api.renderCartPage) {
      api.renderCartPage();
    }
  };

  api.updateQuantity = (w, id, quantity) => {
    const wh = normalizeWarehouse(w);
    const cart = read(wh);
    const item = cart.find((i) => String(i.id) === String(id));
    if (item) {
      if (quantity <= 0) {
        api.removeItem(wh, id);
      } else {
        item.quantity = Number(quantity);
        write(wh, cart);
        api.renderCounts();
        // If on cart page, trigger re-render
        if (api.renderCartPage) {
          api.renderCartPage();
        }
      }
    }
  };

  // Validate cart items against Shopify to detect deleted/drafted products
  api.validateCart = async (w) => {
    const wh = normalizeWarehouse(w);
    const cart = read(wh);
    if (!cart.length) return;

    let changed = false;
    const updates = await Promise.all(cart.map(async (item) => {
      // If item has no ID, it's invalid
      if (!item.id) return null;

      const info = await fetchVariantInfo(item.id);
      
      // If null, product/variant is gone (404)
      if (info === null) {
        changed = true;
        return null;
      }

      // If we got valid info (check for price property to ensure it's not some other error object)
      if (info && typeof info.price === 'number') {
        let itemChanged = false;
        
        // Update fields if they differ and new data is valid
        if (info.price !== item.price) {
          item.price = info.price;
          itemChanged = true;
        }
        if (info.image && info.image !== item.image) {
          item.image = info.image;
          itemChanged = true;
        }
        if (info.variant_title && info.variant_title !== item.variant_title) {
          item.variant_title = info.variant_title;
          itemChanged = true;
        }

        if (itemChanged) changed = true;
      }
      return item;
    }));

    const validItems = updates.filter(Boolean);

    if (changed || validItems.length !== cart.length) {
      write(wh, validItems);
      api.renderCounts();
      // Only re-render cart page if we are currently viewing it
      if (window.location.pathname.includes('/cart')) {
        api.renderCartPage();
      }
    }
  };

  api.renderCounts = () => {
    const usCount = api.count('us');
    const cnCount = api.count('cn');
    const usEl = document.getElementById('WarehouseCartCountUS');
    const cnEl = document.getElementById('WarehouseCartCountCN');
    if (usEl) usEl.textContent = `(${usCount})`;
    if (cnEl) cnEl.textContent = `(${cnCount})`;
  };

  // Render cart items for cart page
  api.renderCartPage = () => {
    const cartItemsContainer = document.querySelector('#main-cart-items .js-contents');
    if (!cartItemsContainer) return;

    // Get warehouse from URL parameter
    const params = new URLSearchParams(window.location.search);
    const warehouse = normalizeWarehouse(params.get('warehouse') || 'us');
    const items = read(warehouse);

    // Update page title to show which warehouse
    const titleEl = document.querySelector('.title--primary');
    if (titleEl && !titleEl.dataset.originalText) {
      titleEl.dataset.originalText = titleEl.textContent;
    }
    if (titleEl) {
      titleEl.textContent = `${warehouse.toUpperCase()} Warehouse Cart`;
    }

    if (items.length === 0) {
      // Show empty state
      const cartWarnings = document.querySelector('.cart__warnings');
      const cartContents = document.querySelector('.cart__contents');
      if (cartWarnings) cartWarnings.style.display = 'block';
      if (cartContents) cartContents.style.display = 'none';
      return;
    }

    // Hide empty state
    const cartWarnings = document.querySelector('.cart__warnings');
    const cartContents = document.querySelector('.cart__contents');
    if (cartWarnings) cartWarnings.style.display = 'none';
    if (cartContents) cartContents.style.display = 'block';

    // Calculate totals
    let subtotal = 0;
    items.forEach(item => {
      subtotal += (item.price || 0) * (item.quantity || 1);
    });

    // Render items
    const rows = items.map((item, index) => {
      const linePrice = (item.price || 0) * (item.quantity || 1);
      const formattedPrice = formatMoney(item.price || 0);
      const formattedLinePrice = formatMoney(linePrice);
      
      return `
        <tr class="cart-item" id="CartItem-${index + 1}" data-variant-id="${item.id}" data-warehouse="${warehouse}">
          <td class="cart-item__media">
            ${item.image ? `
              <a href="${item.url || '#'}" class="cart-item__link" aria-hidden="true" tabindex="-1"> </a>
              <div class="cart-item__image-container gradient global-media-settings">
                <img src="${item.image}" class="cart-item__image" alt="${item.product_title || 'Product'}" loading="lazy" width="150">
              </div>
            ` : ''}
          </td>
          <td class="cart-item__details">
            <a href="${item.url || '#'}" class="cart-item__name h4 break">${item.product_title || 'Product'}</a>
            <div class="product-option">${formattedPrice}</div>
            ${item.variant_title ? `<div class="product-option">${item.variant_title}</div>` : ''}
            ${item.properties && item.properties.Warehouse ? `<div class="product-option">Ships from: ${item.properties.Warehouse}</div>` : ''}
          </td>
          <td class="cart-item__totals right medium-hide large-up-hide">
            <div class="cart-item__price-wrapper">
              <span class="price price--end">${formattedLinePrice}</span>
            </div>
          </td>
          <td class="cart-item__quantity">
            <div class="cart-item__quantity-wrapper">
              <div class="quantity cart-quantity">
                <button class="quantity__button" name="minus" type="button" data-action="decrease" data-variant-id="${item.id}" data-warehouse="${warehouse}">
                  <span class="visually-hidden">Decrease quantity</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 2" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path></svg>
                </button>
                <input class="quantity__input" type="number" value="${item.quantity || 1}" min="0" data-variant-id="${item.id}" data-warehouse="${warehouse}">
                <button class="quantity__button" name="plus" type="button" data-action="increase" data-variant-id="${item.id}" data-warehouse="${warehouse}">
                  <span class="visually-hidden">Increase quantity</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path></svg>
                </button>
              </div>
              <button class="cart-remove-button button button--tertiary" type="button" data-variant-id="${item.id}" data-warehouse="${warehouse}" aria-label="Remove ${item.product_title || 'item'}">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none"><path d="M14 2L2 14M2 2l12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </td>
          <td class="cart-item__totals right small-hide">
            <div class="cart-item__price-wrapper">
              <span class="price price--end">${formattedLinePrice}</span>
            </div>
          </td>
        </tr>
      `;
    }).join('');

    cartItemsContainer.innerHTML = `
      <table class="cart-items">
        <caption class="visually-hidden">Shopping cart</caption>
        <thead>
          <tr>
            <th class="caption-with-letter-spacing" colspan="2" scope="col">Product</th>
            <th class="medium-hide large-up-hide right caption-with-letter-spacing" colspan="1" scope="col">Total</th>
            <th class="cart-items__heading--wide cart-items__heading--quantity small-hide caption-with-letter-spacing" colspan="1" scope="col">Quantity</th>
            <th class="small-hide right caption-with-letter-spacing" colspan="1" scope="col">Total</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    `;

    // Update totals section
    updateCartTotals(subtotal, warehouse);

    // Bind event handlers
    bindCartPageEvents();
  };

  // Helper function to format money (cents to dollar display)
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  };

  // Update cart totals in the footer
  const updateCartTotals = (subtotal, warehouse) => {
    const totalsSection = document.querySelector('.cart__footer');
    if (!totalsSection) return;

    const formattedSubtotal = formatMoney(subtotal);
    
    // Find or create totals display
    let totalsDisplay = totalsSection.querySelector('.totals');
    if (!totalsDisplay) {
      totalsDisplay = document.createElement('div');
      totalsDisplay.className = 'totals';
      totalsSection.prepend(totalsDisplay);
    }

    totalsDisplay.innerHTML = `
      <h2 class="totals__total">Estimated total</h2>
      <p class="totals__total-value">${formattedSubtotal}</p>
    `;

    // Update checkout button to use dual cart logic
    const checkoutBtn = totalsSection.querySelector('button[name="checkout"]');
    if (checkoutBtn && !checkoutBtn.dataset.dualCartBound) {
      checkoutBtn.dataset.dualCartBound = 'true';
      checkoutBtn.addEventListener('click', (e) => {
        e.preventDefault();
        api.syncAndCheckout(warehouse);
      });
    }
  };

  // Bind event handlers for cart page
  const bindCartPageEvents = () => {
    const cartItemsContainer = document.querySelector('#main-cart-items');
    if (!cartItemsContainer || cartItemsContainer.dataset.eventsBound) return;
    
    cartItemsContainer.dataset.eventsBound = 'true';

    // Quantity button clicks
    cartItemsContainer.addEventListener('click', (e) => {
      const button = e.target.closest('button');
      if (!button) return;

      const variantId = button.dataset.variantId;
      const warehouse = button.dataset.warehouse;
      const action = button.dataset.action;

      if (!variantId || !warehouse) return;

      if (button.classList.contains('cart-remove-button')) {
        // Remove item
        api.removeItem(warehouse, variantId);
      } else if (action === 'increase' || action === 'decrease') {
        // Update quantity
        const input = button.closest('.cart-quantity').querySelector('input');
        let qty = parseInt(input.value) || 0;
        qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
        api.updateQuantity(warehouse, variantId, qty);
      }
    });

    // Quantity input changes
    cartItemsContainer.addEventListener('change', (e) => {
      if (!e.target.classList.contains('quantity__input')) return;

      const variantId = e.target.dataset.variantId;
      const warehouse = e.target.dataset.warehouse;
      const qty = parseInt(e.target.value) || 0;

      if (variantId && warehouse) {
        api.updateQuantity(warehouse, variantId, qty);
      }
    });
  };

  api.notify = (text) => {
    try {
      let toast = document.getElementById('dual-cart-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'dual-cart-toast';
        toast.style.position = 'fixed';
        toast.style.top = '1rem';
        toast.style.right = '1rem';
        toast.style.zIndex = '999';
        toast.style.background = 'var(--color-background, #fff)';
        toast.style.border = '1px solid rgba(0,0,0,.12)';
        toast.style.padding = '.6rem .8rem';
        toast.style.borderRadius = '.4rem';
        toast.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        document.body.appendChild(toast);
      }
      toast.textContent = text;
      toast.style.display = 'block';
      setTimeout(() => (toast.style.display = 'none'), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  api.syncAndCheckout = async (w) => {
    const wh = normalizeWarehouse(w);
    const items = read(wh);
    if (!items.length) {
      api.notify(`Your ${wh.toUpperCase()} cart is empty.`);
      return;
    }

    const routes = window.routes || { cart_add_url: '/cart/add.js', cart_clear_url: '/cart/clear.js' };
    try {
      await fetch(routes.cart_clear_url || '/cart/clear.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      for (const { id, quantity, properties } of items) {
        const fd = new FormData();
        fd.append('id', id);
        fd.append('quantity', quantity);
        Object.entries(properties || {}).forEach(([k, v]) => fd.append(`properties[${k}]`, v));
        await fetch(routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
      }

      window.location.href = '/checkout';
    } catch (err) {
      console.error('Dual cart checkout failed', err);
      api.notify('Checkout failed. Please try again.');
    }
  };

  // Added: sync selected warehouse cart to Shopify, then open cart page
  api.syncAndGoToCart = async (w) => {
    const wh = normalizeWarehouse(w);
    const items = read(wh);
    if (!items.length) {
      api.notify(`Your ${wh.toUpperCase()} cart is empty.`);
      return;
    }

    const routes = window.routes || {
      cart_add_url: '/cart/add.js',
      cart_clear_url: '/cart/clear.js',
      cart_url: '/cart'
    };

    try {
      // Clear real Shopify cart
      await fetch(routes.cart_clear_url || '/cart/clear.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      // Add each virtual item back into Shopify cart
      for (const { id, quantity, properties } of items) {
        const fd = new FormData();
        fd.append('id', id);
        fd.append('quantity', quantity);
        Object.entries(properties || {}).forEach(([k, v]) =>
          fd.append(`properties[${k}]`, v)
        );
        await fetch(routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
      }

      // Go to cart page with the warehouse indicator
      const target = `${routes.cart_url || '/cart'}?warehouse=${wh}`;
      window.location.href = target;
    } catch (err) {
      console.error('Dual cart view-cart sync failed', err);
      api.notify('Unable to open cart. Please try again.');
    }
  };

  // Intercept product-form submits before Dawn's handler
  document.addEventListener(
    'submit',
    async (evt) => {
      const form = evt.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[data-type="add-to-cart-form"]')) return;
      evt.preventDefault();
      evt.stopPropagation();

      try {
        // Try to detect warehouse from multiple sources
        const pf = form.closest('product-form');
        let wh = null;

        // 1. Check data-warehouse attribute
        if (pf?.dataset.warehouse) {
          wh = normalizeWarehouse(pf.dataset.warehouse);
        }

        // 2. Try to detect from product tags
        if (!wh || wh === normalizeWarehouse('')) {
          const detectedWh = detectWarehouseFromTags(form);
          if (detectedWh) wh = detectedWh;
        }

        // 3. Default to US if nothing found
        if (!wh) wh = 'us';

        const id = form.querySelector('input[name="id"]')?.value;
        const quantity = Number(form.querySelector('input[name="quantity"]')?.value || 1);
        if (!id) return;
        
        // Show loading notification
        api.notify('Adding to cart...');
        
        // Try to extract product data from the page
        const productInfo = extractProductDataFromPage(form);
        console.log('[Dual Cart] Product data from page:', productInfo);
        
        // Await the addItem with product info
        await api.addItem(wh, { id, quantity, ...productInfo });
        api.notify(`Added to ${wh.toUpperCase()} warehouse cart`);
        
        // Open the drawer after adding
        if (api.openDrawer) {
          setTimeout(() => api.openDrawer(wh), 300);
        }
      } catch (err) {
        console.error(err);
        api.notify('Add to cart failed');
      }
    },
    true
  );

  // Intercept checkout click; choose which warehouse to sync
  const bindCheckout = () => {
    document.querySelectorAll('button[name="checkout"], #checkout').forEach((btn) => {
      if (btn.dataset.dualCartBound === 'true') return;
      btn.dataset.dualCartBound = 'true';
      btn.addEventListener('click', (evt) => {
        evt.preventDefault();
        const usCount = api.count('us');
        const cnCount = api.count('cn');
        const params = new URLSearchParams(window.location.search);
        const ctx = params.get('warehouse');
        let target = normalizeWarehouse(ctx || api.getActiveWarehouse());

        if (!usCount && !cnCount) {
          api.notify('Your carts are empty.');
          return;
        }
        if (target === 'us' && !usCount && cnCount) target = 'cn';
        if (target === 'cn' && !cnCount && usCount) target = 'us';
        api.syncAndCheckout(target);
      });
    });
  };

  const bindHeaderLinks = () => {
    // Updated selector for new header structure
    document.querySelectorAll('.dual-cart-link[data-warehouse]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wh = btn.dataset.warehouse;
        api.setActiveWarehouse(wh);
        if (api.openDrawer) {
          api.openDrawer(wh);
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    api.renderCounts();
    bindCheckout();
    bindHeaderLinks();
    
    // Validate carts (update info / remove deleted)
    // Use timeout to avoid blocking initial render
    setTimeout(() => {
      api.validateCart('us');
      api.validateCart('cn');
    }, 500);
    
    // Initialize cart page if we're on it
    if (window.location.pathname.includes('/cart')) {
      api.renderCartPage();
    }
  });
})();